import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { useUser } from './UserContext';
import { useDataMode } from './DataModeContext';
import { useCompany } from './CompanyContext';
import { getUserRoleService, type UserRolePermissions, type MyPermissionsResponse } from '@/services/userRoleService';
import { ADMINISTRATOR_DEFAULT_PERMISSIONS, USER_DEFAULT_PERMISSIONS } from '@/services/defaultRolePermissions';

interface PermissionsContextType {
  permissions: UserRolePermissions | null;
  isLoading: boolean;
  hasFullAccess: boolean;
  canViewReport: (reportKey: string) => boolean;
  canExportReport: (reportKey: string) => boolean;
  getReportViewAccessLevel: (reportKey: string) => 'own' | 'team' | 'all';
  canViewNavItem: (navKey: string) => boolean;
  canViewSettingsTab: (settingsKey: string) => boolean;
  canViewResourceDirectory: () => boolean;
  canViewResourceDetails: () => boolean;
  getResourceDirectoryViewAccessLevel: () => 'own' | 'team' | 'all';
  canManageIntegrations: () => boolean;
  canViewIntegrations: () => boolean;
  canManageCompanies: () => boolean;
  canViewCompanies: () => boolean;
  canViewRolesPermissions: () => boolean;
  canAccessSettings: () => boolean;
  canCreateBooking: () => boolean;
  canEditBooking: () => boolean;
  canDeleteBooking: () => boolean;
  canOverrideReadOnly: () => boolean;
  refreshPermissions: () => Promise<void>;
}

const PermissionsContext = createContext<PermissionsContextType | undefined>(undefined);

export function PermissionsProvider({ children }: { children: ReactNode }) {
  const { user } = useUser();
  const { getScreenMode } = useDataMode();
  const { currentCompany, isLoading: isCompanyLoading } = useCompany();
  const [permissions, setPermissions] = useState<UserRolePermissions | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [hasFullAccess, setHasFullAccess] = useState(false);

  const mode = getScreenMode('calendar');

  const fetchPermissions = useCallback(async () => {
    setIsLoading(true);
    try {
      const service = getUserRoleService(mode);
      const response: MyPermissionsResponse = await service.getMyPermissions();
      setPermissions(response.permissions);
      setHasFullAccess(
        response.isAdminFallback ||
        response.roleName === 'Administrator'
      );
    } catch (error) {
      console.warn('[PermissionsContext] Failed to fetch permissions, using role-based fallback', error);
      // If we cannot fetch permissions, check workspace role from UserContext
      if (user.workspaceRole === 'Administrator') {
        setPermissions(ADMINISTRATOR_DEFAULT_PERMISSIONS);
        setHasFullAccess(true);
      } else {
        setPermissions(USER_DEFAULT_PERMISSIONS);
        setHasFullAccess(false);
      }
    } finally {
      setIsLoading(false);
    }
  }, [mode, user.workspaceRole, currentCompany?.id]);

  useEffect(() => {
    if (mode === 'live' && isCompanyLoading) return;
    fetchPermissions();
  }, [fetchPermissions, isCompanyLoading, mode]);

  // Refresh permissions when roles are updated (e.g., admin edits a role)
  useEffect(() => {
    const handleRolesUpdated = () => { fetchPermissions(); };
    window.addEventListener('rolesUpdated', handleRolesUpdated);
    return () => { window.removeEventListener('rolesUpdated', handleRolesUpdated); };
  }, [fetchPermissions]);

  // Report visibility is honored from the role even for full-access
  // (Administrator) users, mirroring the navigation / resource directory /
  // booking permissions: an explicit `enabled: false` stored on the role hides
  // the report from the left-hand reports menu, so admins can restrict
  // themselves via the role editor. The admin default of all-enabled preserves
  // visibility unless it is turned off.
  const canViewReport = useCallback((reportKey: string): boolean => {
    if (!permissions?.reportAccessPermissions) return false;
    const report = permissions.reportAccessPermissions[reportKey as keyof typeof permissions.reportAccessPermissions];
    return report?.enabled ?? false;
  }, [permissions]);

  // Export access is honored from the role even for full-access users: an
  // explicit `allowExport: false` hides the export button on the report, so
  // admins can restrict exporting via the role editor. The admin default of
  // all-enabled preserves the export button unless it is turned off.
  const canExportReport = useCallback((reportKey: string): boolean => {
    if (!permissions?.reportAccessPermissions) return false;
    const report = permissions.reportAccessPermissions[reportKey as keyof typeof permissions.reportAccessPermissions];
    return report?.allowExport ?? false;
  }, [permissions]);

  // Data-visibility scope for a report, honored from the role for all users
  // (including full-access Administrators, whose default is 'all'): controls
  // whether the report shows every record ('all'), the logged-in user's
  // department ('team'), or only their own data ('own'). Defaults to the most
  // restrictive 'own' when the role has no explicit value.
  const getReportViewAccessLevel = useCallback((reportKey: string): 'own' | 'team' | 'all' => {
    const report = permissions?.reportAccessPermissions?.[reportKey as keyof NonNullable<typeof permissions.reportAccessPermissions>];
    const level = report?.viewAccess;
    return level === 'all' || level === 'team' || level === 'own' ? level : 'own';
  }, [permissions]);

  // Navigation permissions are honored from the role even for full-access
  // (Administrator) users, mirroring the resource directory / booking
  // permissions: an explicit `false` stored on the role hides the menu item, so
  // admins can restrict themselves via the role editor. The admin default of
  // all-enabled preserves visibility unless it is turned off.
  const canViewNavItem = useCallback((navKey: string): boolean => {
    if (!permissions?.navigationPermissions) return false;
    return (permissions.navigationPermissions as Record<string, boolean>)[navKey] ?? false;
  }, [permissions]);

  // Settings-tab permissions are honored from the role even for full-access
  // (Administrator) users, mirroring the navigation / resource directory /
  // booking permissions: an explicit `false` stored on the role hides the
  // settings menu item (and its page), so admins can restrict themselves via
  // the role editor. The admin default of all-enabled preserves visibility
  // unless it is turned off, and `viewRolesPermissions` is force-enabled for
  // the Administrator role on save so admins can never lock themselves out of
  // the role editor.
  const canViewSettingsTab = useCallback((settingsKey: string): boolean => {
    if (!permissions?.settingsPermissions) return false;
    return (permissions.settingsPermissions as Record<string, boolean>)[settingsKey] ?? false;
  }, [permissions]);

  // Resource directory permissions are honored from the role even for full-access
  // (Administrator) users, mirroring the booking permissions below: an explicit
  // `false` stored on the role denies access, so admins can restrict themselves
  // via the role editor. The admin default of all-enabled preserves visibility
  // unless it is turned off.
  const canViewResourceDirectory = useCallback((): boolean => {
    return permissions?.resourceDirectoryPermissions?.viewDirectory ?? false;
  }, [permissions]);

  const canViewResourceDetails = useCallback((): boolean => {
    return permissions?.resourceDirectoryPermissions?.viewDetails ?? false;
  }, [permissions]);

  // Access level controlling which resource cards a user can see. Not
  // short-circuited on hasFullAccess so admins are bound by the setting too;
  // the admin default of 'all' already grants full visibility unless changed.
  const getResourceDirectoryViewAccessLevel = useCallback((): 'own' | 'team' | 'all' => {
    return (permissions?.resourceDirectoryPermissions?.viewAccess ?? 'own') as 'own' | 'team' | 'all';
  }, [permissions]);

  // Integrations & Company Management permissions are honored from the role even
  // for full-access (Administrator) users, mirroring the reports / navigation /
  // booking permissions above: an explicit `false` stored on the role hides the
  // page/controls, so admins can restrict themselves via the role editor. The
  // admin default of all-enabled preserves access unless it is turned off.
  const canManageIntegrations = useCallback((): boolean => {
    return permissions?.integrationsPermissions?.manageIntegrations ?? false;
  }, [permissions]);

  const canViewIntegrations = useCallback((): boolean => {
    return permissions?.integrationsPermissions?.viewIntegrations ?? false;
  }, [permissions]);

  const canManageCompanies = useCallback((): boolean => {
    return permissions?.companyManagementPermissions?.manageCompanies ?? false;
  }, [permissions]);

  const canViewCompanies = useCallback((): boolean => {
    return permissions?.companyManagementPermissions?.viewCompanies ?? false;
  }, [permissions]);

  const canViewRolesPermissions = useCallback((): boolean => {
    return permissions?.settingsPermissions?.viewRolesPermissions ?? false;
  }, [permissions]);

  const canAccessSettings = useCallback((): boolean => {
    if (!permissions?.settingsPermissions) return false;
    const sp = permissions.settingsPermissions;
    return sp.viewGeneral || sp.viewTimezone || sp.viewBookingTypeSetup || sp.viewGroupSetup || sp.viewRolesPermissions || sp.viewUserSetup;
  }, [permissions]);

  // Booking permissions are honored from the role even for full-access
  // (Administrator) users: an explicit `false` stored on the role denies the
  // action, so admins can restrict themselves via the role editor. A missing
  // key (e.g. roles created before booking permissions existed) defaults to
  // allowed so existing users are not blocked.
  const canCreateBooking = useCallback((): boolean => {
    return permissions?.bookingPermissions?.createBooking ?? true;
  }, [permissions]);

  const canEditBooking = useCallback((): boolean => {
    return permissions?.bookingPermissions?.editBooking ?? true;
  }, [permissions]);

  const canDeleteBooking = useCallback((): boolean => {
    return permissions?.bookingPermissions?.deleteBooking ?? true;
  }, [permissions]);

  const canOverrideReadOnly = useCallback((): boolean => {
    return permissions?.bookingPermissions?.overrideReadOnly ?? true;
  }, [permissions]);

  return (
    <PermissionsContext.Provider
      value={{
        permissions,
        isLoading,
        hasFullAccess,
        canViewReport,
        canExportReport,
        getReportViewAccessLevel,
        canViewNavItem,
        canViewSettingsTab,
        canViewResourceDirectory,
        canViewResourceDetails,
        getResourceDirectoryViewAccessLevel,
        canManageIntegrations,
        canViewIntegrations,
        canManageCompanies,
        canViewCompanies,
        canViewRolesPermissions,
        canAccessSettings,
        canCreateBooking,
        canEditBooking,
        canDeleteBooking,
        canOverrideReadOnly,
        refreshPermissions: fetchPermissions,
      }}
    >
      {children}
    </PermissionsContext.Provider>
  );
}

export function usePermissions() {
  const context = useContext(PermissionsContext);
  if (context === undefined) {
    throw new Error('usePermissions must be used within a PermissionsProvider');
  }
  return context;
}
